import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import styled from "styled-components";
import { DialogTitle, DialogContent, DialogActions, Icon } from "@material-ui/core";
import { TYPEFORM_IDS } from "common/constants";
import { WorkspaceUserRole, WorkspacePlanTier } from "common/interfaces";
import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { defaultText } from "js/textStyles";
import { themeColors } from "js/react/sharedStyles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { getLogger, LogGroup } from "js/core/logger";
import { getStaticUrl } from "js/config";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { app } from "js/namespaces";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { ShowTypeformDialog } from "js/react/components/Dialogs/TypeformDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10 } from "js/react/components/Gap";

const logger = getLogger(LogGroup.BILLING);

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        padding: 40px 50px 5px 50px;
    }

    h2 {
        color: #333;
        text-align: center;
        font-family: "Source Sans Pro";
        font-size: 20px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;

        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;

        p {
            color: #666;
            font-size: 14px;
            font-weight: 400;
            line-height: 125%; 
        }
    }
`;

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    padding: 0px;
    ${defaultText}
`;

const StyledDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        justify-content: space-between;
        padding: 30px 50px 40px 50px;
        margin: 0px;
    }
`;

const RoleText = styled.div`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 110%;
`;

const BillingDateText = styled.div`
    color: #666;
    text-align: center;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%; 
`;

interface CancelOrDowngradePlanDialogProps {
    action: "cancel" | "downgrade";
    closeDialog: () => void;
}

const CancelOrDowngradePlanDialogBase = (props: CancelOrDowngradePlanDialogProps & WorkspaceControllerState & BillingControllerState) => {
    const { action, users, plan: { tier }, stripeData: { subscription }, closeDialog } = props;
    const { t } = useTranslation();

    const isInTrial = subscription.status === "trialing";
    const guestsCount = users.filter(user => user.role === WorkspaceUserRole.GUEST)?.length;
    const membersCount = users.filter(user => user.role === WorkspaceUserRole.MEMBER)?.length;
    const librariansCount = users.filter(user => user.role === WorkspaceUserRole.LIBRARIAN)?.length;
    const ownersCount = users.filter(user => user.role === WorkspaceUserRole.OWNER)?.length;
    const nextBillDate = moment.unix(subscription?.current_period_end).format("MMM DD, YYYY");

    const getTitle = () => {
        const header = (action === "cancel")
            ? t("Are you sure you want to cancel your plan?")
            : t("Are you sure you want to downgrade your plan?");
        const body = (action === "cancel")
            ? t("You will no longer be able to create or edit presentations. All other user access to this workspace will be revoked, including presentations and features.")
            : t("When downgraded, you will lose access to Team features and shared resource libraries. All team members will be converted to Guests. Guests can collaborate on presentations they're invited to, but they cannot create their own presentations.");

        return (
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-downgrade-logo.svg")}/>
                <Gap10/>
                <div data-test-id="cancel-or-downgrade-plan-dialog-title">{header}</div>
                <p data-test-id="cancel-or-downgrade-plan-dialog-body">{body}</p>
            </StyledDialogTitle>
        );
    };

    const getWorkspaceUsersBox = () => {
        if (tier === WorkspacePlanTier.PRO) {
            return (
                <FlexBox left center padding="15px"
                    styles={{ backgroundColor: "#F7FAFC", border: "1px solid #DAE9F0" }}>
                    <Icon color="error" fontSize="small">remove_circle</Icon>
                    <div>Guests: {guestsCount}</div>
                </FlexBox>
            );
        } else {
            return (
                <FlexBox left center padding="15px" gap={20}
                    style={{ background: "#F7FAFC", border: "1px solid #DAE9F0" }}>
                    <FlexBox left column gap={15}>
                        <FlexBox gap={8}>
                            <Icon color="error" fontSize="small">remove_circle</Icon>
                            <RoleText>Owners: <span style={{ fontWeight: 600 }}>{ownersCount}</span></RoleText>
                        </FlexBox>
                        <FlexBox gap={8}>
                            <Icon color="error" fontSize="small">remove_circle</Icon>
                            <RoleText>Librarians: <span style={{ fontWeight: 600 }}>{librariansCount}</span></RoleText>
                        </FlexBox>
                    </FlexBox>
                    <FlexBox left column gap={15}>
                        <FlexBox gap={8}>
                            <Icon color="error" fontSize="small">remove_circle</Icon>
                            <RoleText>Members: <span style={{ fontWeight: 600 }}>{membersCount}</span></RoleText>
                        </FlexBox>
                        <FlexBox gap={8}>
                            <Icon color="error" fontSize="small">remove_circle</Icon>
                            <RoleText>Guests: <span style={{ fontWeight: 600 }}>{guestsCount}</span></RoleText>
                        </FlexBox>
                    </FlexBox>
                </FlexBox>
            );
        }
    };
    const handleDowngradePlan = async () => {
        closeDialog();
        const progressDialog = ShowDialog(ProgressDialog, { title: t(`Downgrading ${tier} plan...`) });
        try {
            await BillingController.downgradeSubscription(WorkspacePlanTier.PRO);
            await WorkspaceController.forceRefreshWorkspace();
            Analytics.ModifyWorkspacePlan.DowngradeFlowComplete({ downgrade_plan_to: WorkspacePlanTier.PRO });
        } catch (err) {
            logger.error(err, "[CancelOrDowngradePlanDialog] handleDowngradePlan() failed");

            ShowErrorDialog({
                error: `An error occurred while downgrading your ${tier} plan`,
                message: (
                    <>
                        <p><strong>Error: </strong>{err.message}</p>
                        <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                    </>
                ),
            });
        } finally {
            progressDialog.close();
        }
    };

    const handleConfirm = async () => {
        if (action === "cancel") {
            try {
                await withProgressDialog({
                    title: `Canceling ${tier} plan...`,
                    action: () => BillingController.cancelSubscription(),
                });
                Analytics.ModifyWorkspacePlan.CancelFlowComplete();

                ShowTypeformDialog({
                    typeformId: TYPEFORM_IDS.CHURN_SURVEY,
                    hiddenFields: {
                        user_id: app.user.id,
                    },
                });
            } catch (err) {
                logger.error(err, "[CancelOrDowngradePlanDialog] handleCancelPlan() failed");

                ShowErrorDialog({
                    error: `An error occurred while canceling your ${tier} plan`,
                    message: (
                        <>
                            <p><strong>Error: </strong>{err.message}</p>
                            <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                        </>
                    ),
                });
            } finally {
                closeDialog();
            }
        } else {
            await handleDowngradePlan();
        }
    };

    const billingMessages = {
        "cancel-trial": t("Your trial will be canceled immediately."),
        "cancel-active": `${t("Your plan will be canceled at the end of the current billing period")}, ${nextBillDate}.`,
        "downgrade-trial": t("Your trial will be downgraded immediately."),
        "downgrade-active": `${t("Your plan will be downgraded at the end of the current billing period")}, ${nextBillDate}.`,
    };
    const actionKey = action === "cancel" ? "cancel" : "downgrade";

    const billingDateText = billingMessages[`${actionKey}-${isInTrial ? "trial" : "active"}`];

    return (
        <BeautifulDialog closeDialog={closeDialog}>
            {getTitle()}
            <StyledDialogContent>
                {getWorkspaceUsersBox()}
                <BillingDateText data-test-id="cancel-or-downgrade-plan-dialog-date-text">
                    {billingDateText}
                </BillingDateText>
            </StyledDialogContent>
            <StyledDialogActions>
                <Button onClick={closeDialog} unframed large color={themeColors.mediumGray}>
                    Not now
                </Button>
                <Button warning large onClick={handleConfirm}>
                    {action === "cancel" ? "Cancel Plan" : "Downgrade Plan"}
                </Button>
            </StyledDialogActions>
        </BeautifulDialog>
    );
};

export default WorkspaceController.withInitializedState(BillingController.withInitializedState(CancelOrDowngradePlanDialogBase));
export { CancelOrDowngradePlanDialogBase };
